import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Seo from '../../seo/Seo';
import Icon from '../home/Icon';
import {
  MODULES, MODULE_KEYS, MODULE_BY_KEY, BUSINESS_TYPES, DEFAULT_BUSINESS_TYPE,
  WORKED_EXAMPLES, SMALL_PRINT, PRICING_FAQS, RATES, CURRENCY, LIST_RATE,
  EVERYTHING_DISCOUNT_PCT, BULK_THRESHOLD, PRICE_SHEET_DATE,
  quote, normaliseModules, softwareApplicationSchema,
} from './pricingData';
import './Pricing.css';

const SLIDER_MAX = 50;

const money = (n) => `${CURRENCY}${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;

// Content is visible by default; the observer only layers an entrance on top.
const useReveal = () => {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.rp-reveal'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    const failsafe = setTimeout(() => els.forEach((el) => el.classList.add('in')), 1200);
    return () => { io.disconnect(); clearTimeout(failsafe); };
  }, []);
};

/* ─── Estimator controls ─────────────────────────────────────────────────── */

const Stepper = ({ label, sub, value, min, max, onChange }) => {
  const clamp = (n) => Math.min(max, Math.max(min, n));
  return (
    <div className="rp-stepper">
      <div className="rp-stepper-text">
        <span className="rp-stepper-label">{label}</span>
        {sub && <span className="rp-stepper-sub">{sub}</span>}
      </div>
      <div className="rp-stepper-control">
        <button type="button" className="rp-step-btn" onClick={() => onChange(clamp(value - 1))}
          disabled={value <= min} aria-label={`Decrease ${label}`}>&minus;</button>
        <input
          type="number"
          className="rp-step-input"
          value={value}
          min={min}
          max={max}
          onChange={(e) => onChange(clamp(parseInt(e.target.value, 10) || min))}
          aria-label={label}
        />
        <button type="button" className="rp-step-btn" onClick={() => onChange(clamp(value + 1))}
          disabled={value >= max} aria-label={`Increase ${label}`}>+</button>
      </div>
    </div>
  );
};

/* ─── Rate-card pieces ───────────────────────────────────────────────────── */

const ModuleCard = ({ module: m }) => (
  <article className={`rp-card ${m.kind === 'base' ? 'is-base' : ''}`} style={{ '--card': m.color }}>
    <header className="rp-card-head">
      <span className="rp-card-icon" style={{ background: m.tint, color: m.color }}>
        <Icon name={m.icon} size={19} />
      </span>
      <span className="rp-card-kind">{m.kind === 'base' ? 'Base' : 'Add-on'}</span>
    </header>
    <h3 className="rp-card-name">{m.name}</h3>
    <p className="rp-card-tag">{m.tagline}</p>
    <div className="rp-card-price">
      <span className="rp-card-amount">{m.kind === 'addon' ? '+' : ''}{money(m.base)}</span>
      <span className="rp-card-unit">per user, per month</span>
    </div>
    {m.kind === 'base' && (
      <p className="rp-card-alt">Joining an existing plan? <strong>+{money(m.addOn)}</strong> per user.</p>
    )}
    <ul className="rp-card-list">
      {m.includes.map((f) => (
        <li key={f}><span className="rp-tick" aria-hidden="true">✓</span>{f}</li>
      ))}
    </ul>
    {m.note && <p className="rp-card-note">{m.note}</p>}
  </article>
);

const Band = ({ label, note, columns, children }) => (
  <section className="rp-band rp-reveal">
    <header className="rp-band-head">
      <span className="rp-band-label">{label}</span>
      {note && <span className="rp-band-note">{note}</span>}
    </header>
    <div className={`rp-cards rp-cards--${columns}`}>{children}</div>
  </section>
);

const Estimator = () => {
  const navigate = useNavigate();
  const preset = BUSINESS_TYPES.find((b) => b.key === DEFAULT_BUSINESS_TYPE);
  const [businessType, setBusinessType] = useState(preset.key);
  const [modules, setModules] = useState(preset.modules);
  const [users, setUsers] = useState(preset.users);
  const [aiSeats, setAiSeats] = useState(0);
  const [yearly, setYearly] = useState(true);

  const q = useMemo(() => quote({ modules, users, aiSeats, yearly }), [modules, users, aiSeats, yearly]);

  const pickBusiness = (b) => {
    setBusinessType(b.key);
    setModules(b.modules);
    setUsers(b.users);
    setAiSeats((n) => Math.min(n, b.users));
  };

  // Production needs ERP Standard, so the two chips move together.
  const toggleModule = (key) => {
    const on = modules.includes(key);
    let next;
    if (on) {
      next = modules.filter((k) => k !== key);
      if (key === 'erp') next = next.filter((k) => k !== 'prod');
    } else {
      next = normaliseModules([...modules, key]);
    }
    setModules(next);
    setBusinessType('custom'); // touching the modules makes it your own plan
  };

  const setUserCount = (n) => {
    setUsers(n);
    setAiSeats((seats) => Math.min(seats, n));
  };

  const activeBusiness = BUSINESS_TYPES.find((b) => b.key === businessType);

  return (
    <div className="rp-estimator" id="estimate">
      <div className="rp-estimator-controls">
        {/* ① Nature of business */}
        <div className="rp-field">
          <div className="rp-field-head">
            <span className="rp-field-num">1</span>
            <div>
              <h3 className="rp-field-title">What kind of business are you?</h3>
              <p className="rp-field-hint">
                {activeBusiness ? activeBusiness.blurb : 'Your own mix — pick whatever you need below.'}
              </p>
            </div>
          </div>
          <div className="rp-chips">
            {BUSINESS_TYPES.map((b) => (
              <button
                key={b.key}
                type="button"
                className={`rp-chip ${businessType === b.key ? 'is-on' : ''}`}
                aria-pressed={businessType === b.key}
                onClick={() => pickBusiness(b)}
              >
                <Icon name={b.icon} size={15} />
                {b.label}
              </button>
            ))}
            <button
              type="button"
              className={`rp-chip ${businessType === 'custom' ? 'is-on' : ''}`}
              aria-pressed={businessType === 'custom'}
              onClick={() => setBusinessType('custom')}
            >
              <Icon name="cog" size={15} />Custom
            </button>
          </div>
        </div>

        {/* ② Modules */}
        <div className="rp-field">
          <div className="rp-field-head">
            <span className="rp-field-num">2</span>
            <div>
              <h3 className="rp-field-title">Which modules do you need?</h3>
              <p className="rp-field-hint">Switch any add-on on or off, any month.</p>
            </div>
          </div>
          <div className="rp-modchips">
            {MODULES.map((m) => {
              const on = modules.includes(m.key);
              const delta = on
                ? (q.breakdown.find((l) => l.key === m.key) || {}).amount
                : quote({ modules: [...modules, m.key], users: 1 }).rate - q.rate;
              return (
                <button
                  key={m.key}
                  type="button"
                  className={`rp-modchip ${on ? 'is-on' : ''}`}
                  aria-pressed={on}
                  onClick={() => toggleModule(m.key)}
                  style={on ? { '--chip': m.color, '--chip-tint': m.tint } : undefined}
                >
                  <span className="rp-modchip-icon" style={{ background: m.tint, color: m.color }}>
                    <Icon name={m.icon} size={16} />
                  </span>
                  <span className="rp-modchip-text">
                    <span className="rp-modchip-name">{m.name}</span>
                    <span className="rp-modchip-tag">{m.tagline}</span>
                  </span>
                  <span className="rp-modchip-price">
                    {on ? '' : '+'}{money(Math.max(delta, 0))}
                  </span>
                  <span className="rp-modchip-mark" aria-hidden="true">{on ? '✓' : '+'}</span>
                </button>
              );
            })}
          </div>
          {modules.includes('prod') && (
            <p className="rp-field-note"><Icon name="info" size={13} /> Production runs on Raya ERP Standard, so it is included above.</p>
          )}
        </div>

        {/* ③ People */}
        <div className="rp-field">
          <div className="rp-field-head">
            <span className="rp-field-num">3</span>
            <div>
              <h3 className="rp-field-title">How many people?</h3>
              <p className="rp-field-hint">Every user gets every module you picked, across one company.</p>
            </div>
          </div>

          <Stepper label="Users" value={users} min={1} max={999} onChange={setUserCount} />
          <input
            type="range"
            className="rp-range"
            min={1}
            max={SLIDER_MAX}
            value={Math.min(users, SLIDER_MAX)}
            onChange={(e) => setUserCount(parseInt(e.target.value, 10))}
            aria-label="Number of users"
          />
          <div className="rp-range-scale"><span>1</span><span>{SLIDER_MAX}+</span></div>

          <Stepper
            label="Raya AI seats"
            sub={`Optional · ${money(RATES.ai)} each · give a seat only to the accounts that need one`}
            value={aiSeats}
            min={0}
            max={users}
            onChange={setAiSeats}
          />
        </div>
      </div>

      {/* Live quote */}
      <aside className="rp-quote" aria-live="polite">
        <div className="rp-quote-billing" role="group" aria-label="Billing period">
          <button type="button" className={!yearly ? 'is-on' : ''} onClick={() => setYearly(false)} aria-pressed={!yearly}>Monthly</button>
          <button type="button" className={yearly ? 'is-on' : ''} onClick={() => setYearly(true)} aria-pressed={yearly}>
            Yearly<span className="rp-quote-save">2 months free</span>
          </button>
        </div>

        {q.empty ? (
          <div className="rp-quote-empty">
            <Icon name="layers" size={22} />
            <p>Pick at least one module to see your price.</p>
          </div>
        ) : (
          <>
            <div className="rp-quote-rate">
              <span className="rp-quote-amount">{money(q.rate)}</span>
              <span className="rp-quote-unit">per user, per month</span>
              {q.isEverything && (
                <span className="rp-quote-strike">
                  <s>{money(LIST_RATE)}</s> Everything &middot; {EVERYTHING_DISCOUNT_PCT}% off
                </span>
              )}
            </div>

            <ul className="rp-lines">
              {q.breakdown.map((line) => (
                <li key={line.key}>
                  <span>{line.name}</span>
                  <span className="rp-lines-num">{money(line.amount)}</span>
                </li>
              ))}
              {q.isEverything && (
                <li className="is-discount">
                  <span>Everything bundle</span>
                  <span className="rp-lines-num">&minus;{money(q.bundleDiscount)}</span>
                </li>
              )}
            </ul>

            <ul className="rp-lines rp-lines--totals">
              <li>
                <span>{q.users} {q.users === 1 ? 'user' : 'users'} &times; {money(q.rate)}</span>
                <span className="rp-lines-num">{money(q.moduleMonthly)}</span>
              </li>
              {q.aiSeats > 0 && (
                <li>
                  <span>{q.aiSeats} Raya AI {q.aiSeats === 1 ? 'seat' : 'seats'} &times; {money(RATES.ai)}</span>
                  <span className="rp-lines-num">{money(q.aiMonthly)}</span>
                </li>
              )}
            </ul>

            <div className="rp-quote-total">
              <span className="rp-quote-total-label">{yearly ? 'Billed yearly' : 'Billed monthly'}</span>
              <span className="rp-quote-total-value">{money(yearly ? q.annual : q.monthly)}</span>
              <span className="rp-quote-total-unit">{yearly ? 'a year, for 10 months of use' : 'a month'}</span>
            </div>

            <p className="rp-quote-alt">
              {yearly
                ? <>That is {money(q.monthly)} a month, and you keep {money(q.annualSaving)} a year.</>
                : <>Pay yearly instead and it is {money(q.annual)} — {money(q.annualSaving)} saved.</>}
            </p>

            {q.needsQuote && (
              <p className="rp-quote-bulk">
                <Icon name="info" size={14} />
                Over {BULK_THRESHOLD} users — a bulk discount can be worked out, so this is a ceiling, not the final number.
              </p>
            )}

            <div className="rp-quote-actions">
              <button type="button" className="rp-btn rp-btn--primary" onClick={() => navigate('/register')}>Start free trial</button>
              <button type="button" className="rp-btn rp-btn--outline" onClick={() => navigate('/contact-us')}>
                {q.needsQuote ? 'Get a bulk quote' : 'Talk to sales'}
              </button>
            </div>
            <p className="rp-quote-fine">Exclusive of GST &middot; One company, one PAN &middot; No card needed</p>
          </>
        )}
      </aside>
    </div>
  );
};

/* ─── Page ───────────────────────────────────────────────────────────────── */

const PricingPage = () => {
  const navigate = useNavigate();
  useReveal();

  const examples = WORKED_EXAMPLES.map((ex) => ({ ...ex, q: quote({ ...ex, yearly: true }) }));

  return (
    <div className="rp-page">
      <Seo path="/pricing" faqs={PRICING_FAQS} schema={[softwareApplicationSchema({ detailed: true })]} />

      {/* ── Hero ─────────────────────────────── */}
      <section className="rp-hero">
        <div className="rp-hero-grid" aria-hidden="true" />
        <div className="rp-hero-blob" aria-hidden="true" />
        <div className="rp-container rp-hero-inner">
          <span className="rp-eyebrow">Pricing &middot; {PRICE_SHEET_DATE}</span>
          <h1 className="rp-hero-title">Pricing you can do <em>in your head.</em></h1>
          <p className="rp-hero-sub">
            Per user, per month. Pick your modules, multiply by your team, and that is your bill —
            no platform fee, no company minimum, and no seat you did not ask for.
          </p>
          <p className="rp-hero-note">
            One subscription covers one company &mdash; one PAN, with every GSTIN under it included.
            All prices exclusive of GST.
          </p>
        </div>
      </section>
      {/* ── Rate card ─────────────────────────────── */}
      <section className="rp-section rp-section--tight" id="rate-card">
        <div className="rp-container">
          <Band label="The base" note="Start with either one — both roads meet at ₹599 per user." columns="2">
            {MODULES.filter((m) => m.kind === 'base').map((m) => <ModuleCard key={m.key} module={m} />)}
          </Band>

          <Band label="Add-ons" note="Switch any of these on — or off — any month." columns="3">
            {MODULES.filter((m) => m.kind === 'addon').map((m) => <ModuleCard key={m.key} module={m} />)}
          </Band>

          <Band label="Bundle &amp; extras" note="Take the lot, add AI only where it is needed, and pay yearly." columns="3">
            <article className="rp-card is-feature">
              <header className="rp-card-head">
                <span className="rp-card-icon rp-card-icon--light"><Icon name="layers" size={19} /></span>
                <span className="rp-card-kind">Best value</span>
              </header>
              <h3 className="rp-card-name">Everything</h3>
              <p className="rp-card-tag">All five modules, one rate.</p>
              <div className="rp-card-price">
                <span className="rp-card-amount">{money(RATES.everything)}</span>
                <span className="rp-card-unit">per user, per month</span>
              </div>
              <p className="rp-card-alt">
                <s>{money(LIST_RATE)}</s> separately &mdash; <strong>{EVERYTHING_DISCOUNT_PCT}% off</strong>
              </p>
              <ul className="rp-card-list">
                {MODULE_KEYS.map((k) => (
                  <li key={k}><span className="rp-tick" aria-hidden="true">✓</span>{MODULE_BY_KEY[k].name}</li>
                ))}
              </ul>
            </article>

            <article className="rp-card is-ai">
              <header className="rp-card-head">
                <span className="rp-card-icon" style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                  <Icon name="sparkles" size={19} />
                </span>
                <span className="rp-card-kind">Optional</span>
              </header>
              <h3 className="rp-card-name">Raya AI</h3>
              <p className="rp-card-tag">Ask for work, not just answers. It records, drafts and forecasts.</p>
              <div className="rp-card-price">
                <span className="rp-card-amount">+{money(RATES.ai)}</span>
                <span className="rp-card-unit">per AI seat, per month</span>
              </div>
              <p className="rp-card-alt">
                Add it only to the owners or main accounts that need it &mdash; not the whole team.
              </p>
              <ul className="rp-card-list">
                <li><span className="rp-tick" aria-hidden="true">✓</span>Records bills, expenses and sales</li>
                <li><span className="rp-tick" aria-hidden="true">✓</span>Drafts quotations, finds stock, forecasts</li>
                <li><span className="rp-tick" aria-hidden="true">✓</span>Assign or remove a seat any month</li>
              </ul>
              <p className="rp-card-note rp-card-link">
                <a href="/ai" onClick={(e) => { e.preventDefault(); navigate('/ai'); }}>See what Raya AI can do &rarr;</a>
              </p>
            </article>

            <article className="rp-card is-offer">
              <header className="rp-card-head">
                <span className="rp-card-icon" style={{ background: '#ecfdf5', color: '#059669' }}>
                  <Icon name="wallet" size={19} />
                </span>
                <span className="rp-card-kind">Offer</span>
              </header>
              <h3 className="rp-card-name">Pay yearly</h3>
              <p className="rp-card-tag">Settle the year up front and two months are on us.</p>
              <div className="rp-card-price">
                <span className="rp-card-amount">2 months</span>
                <span className="rp-card-unit">free, on any plan</span>
              </div>
              <p className="rp-card-alt">
                You are billed for <strong>10 months</strong>, not 12 &mdash; add-ons included.
              </p>
              <ul className="rp-card-list">
                <li><span className="rp-tick" aria-hidden="true">✓</span>Applies to every plan and add-on</li>
                <li><span className="rp-tick" aria-hidden="true">✓</span>One invoice a year, not twelve</li>
                <li><span className="rp-tick" aria-hidden="true">✓</span>Go back to monthly at renewal</li>
              </ul>
            </article>
          </Band>
        </div>
      </section>
      {/* ── Estimator ─────────────────────────────── */}
      <section className="rp-section rp-section--alt">
        <div className="rp-container">
          <div className="rp-head rp-head--center rp-reveal">
            <span className="rp-eyebrow">Work out your bill</span>
            <h2 className="rp-h2">Or tell us your business. <em>See your number.</em></h2>
            <p className="rp-lead rp-lead--center">
              Pick the kind of work you do, adjust the modules and set your headcount.
              The price updates as you go — no form, no waiting for a callback.
            </p>
          </div>
          <div className="rp-reveal"><Estimator /></div>
        </div>
      </section>
      {/* ── Worked examples ─────────────────────────────── */}
      <section className="rp-section rp-section--alt">
        <div className="rp-container">
          <div className="rp-head rp-reveal">
            <span className="rp-eyebrow">Worked examples</span>
            <h2 className="rp-h2">How it looks <em>for your wallet.</em></h2>
            <p className="rp-lead">Six typical teams. In every case the bill is simply users &times; rate.</p>
          </div>

          <div className="rp-table rp-reveal" role="table" aria-label="Worked pricing examples">
            <div className="rp-table-head" role="row">
              <span role="columnheader">Team</span>
              <span role="columnheader">Plan</span>
              <span role="columnheader" className="rp-num">Users</span>
              <span role="columnheader" className="rp-num">Per user</span>
              <span role="columnheader" className="rp-num">A month</span>
              <span role="columnheader" className="rp-num">A year, paid yearly</span>
            </div>
            {examples.map((ex) => (
              <div className="rp-table-row" role="row" key={`${ex.label}-${ex.users}`}>
                <span role="cell" className="rp-table-label">{ex.label}</span>
                <span role="cell" className="rp-table-plan">{ex.detail}</span>
                <span role="cell" className="rp-num">{ex.users}</span>
                <span role="cell" className="rp-num">{money(ex.q.rate)}</span>
                <span role="cell" className="rp-num rp-strong">{money(ex.q.monthly)}</span>
                <span role="cell" className="rp-num">{money(ex.q.annual)}</span>
              </div>
            ))}
          </div>
          <p className="rp-table-foot rp-reveal">
            All figures exclusive of GST. Yearly billing charges 10 months, not 12.
          </p>
        </div>
      </section>

      {/* ── Small print ─────────────────────────────── */}
      <section className="rp-section">
        <div className="rp-container">
          <div className="rp-head rp-reveal">
            <span className="rp-eyebrow">The small print</span>
            <h2 className="rp-h2">Nothing hiding <em>below the line.</em></h2>
          </div>
          <div className="rp-print rp-reveal">
            {SMALL_PRINT.map((p) => (
              <div className="rp-print-card" key={p.title}>
                <span className="rp-print-icon"><Icon name={p.icon} size={17} /></span>
                <h3 className="rp-print-title">{p.title}</h3>
                <p className="rp-print-body">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────── */}
      <section className="rp-section rp-section--alt">
        <div className="rp-container rp-container--narrow">
          <div className="rp-head rp-head--center rp-reveal">
            <span className="rp-eyebrow">Questions</span>
            <h2 className="rp-h2">Before you ask.</h2>
          </div>
          <div className="rp-faq rp-reveal">
            {PRICING_FAQS.map((f) => (
              <details className="rp-faq-item" key={f.q}>
                <summary className="rp-faq-q">{f.q}<span className="rp-faq-plus" aria-hidden="true">+</span></summary>
                <p className="rp-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────── */}
      <section className="rp-cta">
        <div className="rp-cta-blob" aria-hidden="true" />
        <div className="rp-container rp-cta-inner">
          <h2 className="rp-cta-title">Start free. Pay when it is running your business.</h2>
          <div className="rp-cta-actions">
            <button type="button" className="rp-btn rp-btn--primary rp-btn--lg" onClick={() => navigate('/register')}>Start free trial</button>
            <button type="button" className="rp-btn rp-btn--outline rp-btn--lg" onClick={() => navigate('/contact-us')}>Book a demo</button>
          </div>
          <p className="rp-cta-meta">Raya Software Solutions &middot; All prices exclusive of GST</p>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
